import { FC, useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { buildTemplatesUrl } from '@/shared/lib';

import { organizationModel } from '@/entities/organization';
import { templatesModel } from '@/entities/templates';

import { EncounterSchemaForm } from '@/features/encounterSchemaEditor';

import styles from './TemplatesEditorPane.module.scss';

type TemplatesSchemaEditorProps = {
  className?: string;
};

export const TemplatesSchemaEditor: FC<TemplatesSchemaEditorProps> = (
  props,
) => {
  const { className } = props;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const schemaId = searchParams.get('id') ?? undefined;
  const importedTemplateSeriesId =
    searchParams.get('imported_template_series_id') ?? undefined;

  const organizationId = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );

  const schema = useAppSelector(
    schemaId
      ? templatesModel.selectors.selectEncounterSchemaById(schemaId)
      : () => undefined,
  );
  const isLoading = useAppSelector(
    templatesModel.selectors.selectEncounterSchemasLoading,
  );

  useEffect(() => {
    if (schemaId) {
      void dispatch(templatesModel.fetchEncounterSchemaById(schemaId));
    }
  }, [dispatch, schemaId]);

  const handleSaveComplete = useCallback(
    (savedSchemaId?: string) => {
      const targetId = savedSchemaId ?? schemaId;

      if (targetId) {
        navigate(buildTemplatesUrl({ specialty: targetId }));
        return;
      }

      navigate(buildTemplatesUrl());
    },
    [navigate, schemaId],
  );

  if (schemaId && !schema && isLoading) {
    return (
      <div className={className}>
        <FormattedMessage id="global.loading" defaultMessage="Loading…" />
      </div>
    );
  }

  return (
    <EncounterSchemaForm
      className={className ?? styles.editor}
      schema={schema}
      organizationId={organizationId}
      importedTemplateSeriesId={importedTemplateSeriesId}
      onSaveComplete={handleSaveComplete}
    />
  );
};
